"use client";

import { useState } from "react";
import { X, Mail, Send, User, FileText, Shield } from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";

export default function EmailShareModal({ isOpen, onClose, soapNote }) {
  const [emailForm, setEmailForm] = useState({
    to: "",
    subject: "",
    message: ""
  });
  const [isSending, setIsSending] = useState(false);

  if (!isOpen) return null;

  const handleInputChange = (field, value) => {
    setEmailForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSendEmail = async () => {
    if (!emailForm.to.trim()) {
      alert("Please enter a recipient email address");
      return;
    }

    setIsSending(true);
    
    try {
      // Here you would integrate with your email service
      console.log("Sending email:", {
        to: emailForm.to,
        subject: emailForm.subject,
        message: emailForm.message,
        soapNote: soapNote
      });
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      alert("SOAP note shared successfully!");
      onClose();
      
      // Reset form
      setEmailForm({
        to: "",
        subject: "",
        message: ""
      });
    } catch (error) {
      console.error("Error sending email:", error);
      alert("Failed to send email. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  const generateDefaultSubject = () => {
    const date = new Date().toLocaleDateString();
    return `SOAP Note - ${date}`;
  };

  const generateDefaultMessage = () => {
    return `Please find the attached SOAP note for your review.

Best regards,
Medical Team`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
              <Mail className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Share SOAP Note</h2>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Security Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-blue-900">Secure Sharing</h3>
                <p className="text-sm text-blue-700 mt-1">
                  This email will be sent securely and the SOAP note will be encrypted in transit.
                </p>
              </div>
            </div>
          </div>

          {/* SOAP Note Preview */}
          {soapNote && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center space-x-2">
                  <FileText className="w-4 h-4" />
                  <span>SOAP Note Preview</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 text-sm">
                  {soapNote.subjective && (
                    <div>
                      <span className="font-medium text-blue-600">Subjective:</span>
                      <p className="text-gray-700 mt-1 line-clamp-2">
                        {soapNote.subjective}
                      </p>
                    </div>
                  )}
                  {soapNote.objective && (
                    <div>
                      <span className="font-medium text-green-600">Objective:</span>
                      <p className="text-gray-700 mt-1 line-clamp-2">
                        {soapNote.objective}
                      </p>
                    </div>
                  )}
                  {soapNote.assessment && (
                    <div>
                      <span className="font-medium text-orange-600">Assessment:</span>
                      <p className="text-gray-700 mt-1 line-clamp-2">
                        {soapNote.assessment}
                      </p>
                    </div>
                  )}
                  {soapNote.plan && (
                    <div>
                      <span className="font-medium text-purple-600">Plan:</span>
                      <p className="text-gray-700 mt-1 line-clamp-2">
                        {soapNote.plan}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Email Form */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Recipient Email *
              </label>
              <Input
                type="email"
                value={emailForm.to}
                onChange={(e) => handleInputChange("to", e.target.value)}
                placeholder="Enter email address"
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subject
              </label>
              <Input
                type="text"
                value={emailForm.subject}
                onChange={(e) => handleInputChange("subject", e.target.value)}
                placeholder={generateDefaultSubject()}
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message
              </label>
              <Textarea
                value={emailForm.message}
                onChange={(e) => handleInputChange("message", e.target.value)}
                placeholder={generateDefaultMessage()}
                rows={4}
                className="w-full"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isSending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSendEmail}
              disabled={isSending || !emailForm.to.trim()}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isSending ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Sending...</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <Send className="w-4 h-4" />
                  <span>Send Email</span>
                </div>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
